import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { GetTaskDefinitionsSearchParametersSchema } from '../../../../common/src/domain/taskDefinition/taskDefinitionPaths';
import { SurveyStates } from '../../../../common/src/enums/SurveyStates';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchDelete } from '../../store/openApiFetch';
import { useOpenApiFetch } from '../../store/useOpenApiFetch';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelFilterSelect,
    EditPanelSection,
    PanelBody,
    PanelRouterLink,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';

type SearchParameters = GetTaskDefinitionsSearchParametersSchema;

export function TaskDefinitionsList() {
    const translate = useTranslate();
    const [searchParameters, setSearchParameters] = useState<SearchParameters>({});
    const navigate = useNavigate();

    const { data, reload, isLoading } = useOpenApiFetch('/api/settings/taskDefinitions', 'get', { searchParameters }, []);

    async function deleteItem(taskDefinitionId: string) {
        await openApiFetchDelete('/api/settings/taskDefinitions/{taskDefinitionId}', {
            taskDefinitionId,
        });
        await reload();
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={translate('workflow.checklist')} icon="fa-clock-o" />
                </TopbarLeft>
                <TopbarRight>
                    <PanelRouterLink
                        to="/settings/taskDefinitions/add"
                        text={translate('common.addItem', translate('workflow.task'))}
                        icon="fa fa-plus"
                        small={true}
                    />
                </TopbarRight>
            </Topbar>
            <Content>
                <Tray>
                    <PanelWithoutForm>
                        <PanelBody noPadding={true}>
                            <Table hover={true}>
                                <TableHead>
                                    <TableHeadRow light={true}>
                                        <TableHeader>{translate('common.name')}</TableHeader>
                                        <TableHeader>{translate('common.status')}</TableHeader>
                                        <TableHeader>{translate('common.order')}</TableHeader>
                                        <TableHeader align="right">&nbsp; </TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                {isLoading || !data ? (
                                    <TableBody>
                                        <TableRow>
                                            <TableData colSpan={2}>
                                                <Spinner />
                                            </TableData>
                                        </TableRow>
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        {data.items.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableData>{item.name}</TableData>
                                                <TableData>{translate(`surveyStatus.${item.surveyStatus}`)}</TableData>
                                                <TableData>{item.order}</TableData>
                                                <TableData>
                                                    <IconEditButton onClick={() => navigate(`/settings/taskDefinitions/${item.id}/edit`)} />
                                                    <IconRemoveButton
                                                        onClick={async () => deleteItem(item.id)}
                                                        removeQuestion={translate('common.confirmDelete', item.name)}
                                                    ></IconRemoveButton>
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </PanelBody>
                    </PanelWithoutForm>
                </Tray>

                <RightTray>
                    <div className="admin-form">
                        <h4> {translate('workflow.filter')}</h4>
                        <EditPanelSection>
                            <EditPanelFilterSelect
                                name="status"
                                value={searchParameters.status}
                                handleChange={(value) => setSearchParameters((i) => ({ ...i, status: value as SurveyStates }))}
                                placeholder={translate('common.selectItem', translate('common.status'))}
                                options={
                                    [
                                        SurveyStates.Open,
                                        SurveyStates.Preparation,
                                        SurveyStates.Conducting,
                                        SurveyStates.Reporting,
                                        SurveyStates.Delivery,
                                    ].map((i) => ({ value: i, text: translate(`surveyStatus.${i}`) })) ?? []
                                }
                            />
                        </EditPanelSection>
                        <EditPanelButtons topBorder>
                            <EditPanelButton onClick={() => setSearchParameters({})} text={translate('common.reset')} />
                        </EditPanelButtons>
                    </div>
                </RightTray>
            </Content>
        </>
    );
}
